<template>
  <div class="cosell-page">
    <div class="cosell-container">
      <div class="cosell-header">
        <div class="header-content">
          <h1 class="page-title">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16">
              <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>
              <path d="M5 11.5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5m0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5m0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5m0-2a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5"/>
            </svg>
            Mazodier IA
          </h1>
          <p class="page-subtitle">Brief commercial quotidien — actions priorisées par LLM</p>
        </div>
        <router-link :to="{ name: 'adminDashboard' }" class="back-button">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path fill-rule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8"/>
          </svg>
          Retour au dashboard
        </router-link>
      </div>

      <div class="owner-selector-card">
        <label class="owner-label">Commercial :</label>
        <div class="owner-tabs">
          <button
            v-for="o in owners"
            :key="o.slug"
            :class="['owner-tab', { active: o.slug === selectedOwner }]"
            @click="selectOwner(o.slug)"
          >{{ o.label }}</button>
        </div>
        <div class="date-display">{{ formatDate(today) }}</div>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>Chargement du brief…</p>
      </div>

      <div v-else-if="!brief" class="empty-state">
        <p>Aucun brief disponible pour {{ selectedOwner }} aujourd'hui.</p>
        <p class="empty-hint">Le pipeline tourne tous les matins à 7h00. Si on est avant 7h ou si le job a échoué, le brief n'existe pas encore.</p>
      </div>

      <div v-else class="brief-content">
        <div class="summary-card">
          <p class="summary-text">{{ brief.summary }}</p>
          <p class="summary-meta">Généré à {{ formatTime(brief.generated_at) }}</p>
        </div>

        <div v-for="section in brief.sections" :key="section.title" class="section-card">
          <h3 class="section-heading" :class="sectionClass(section.title)">
            {{ section.title }}
            <span class="section-count">({{ section.actions?.length || 0 }})</span>
          </h3>

          <div v-for="action in section.actions" :key="action.id" class="action-card" :class="{ 'is-logged': loggedActions[action.id] }">
            <div class="card-top">
              <div class="card-identity">
                <div class="card-org">{{ action.account_name || action.deal_name }}</div>
                <a v-if="action.contact_email" :href="`mailto:${action.contact_email}`" class="card-email">
                  {{ action.contact_email }}
                </a>
              </div>
              <div class="card-amount">{{ formatAmount(action.amount) }}</div>
            </div>

            <div class="card-headline">{{ action.headline }}</div>

            <div class="card-links">
              <a v-if="action.zoho_url" :href="action.zoho_url" target="_blank" rel="noopener" class="card-link">
                Zoho ↗
              </a>
              <router-link
                v-if="cabinetSearchTerm(action)"
                :to="{ name: 'activityPage', query: { search: cabinetSearchTerm(action) } }"
                target="_blank"
                class="card-link"
              >
                👥 Activité cabinet ↗
              </router-link>
              <button
                v-if="action.deal_id"
                @click="openDescEditor(action)"
                class="card-link card-link-button"
                :disabled="descLoading[action.deal_id]"
              >
                ✎ Description Zoho
              </button>
              <button
                v-if="action.deal_id"
                @click="markLost(action)"
                class="card-link card-link-button card-link-danger"
                :disabled="lostLoading[action.deal_id]"
              >
                🪦 Marquer perdu
              </button>
              <button
                v-if="action.deal_id && action.contact_email"
                @click="openDraftEmail(action)"
                class="card-link card-link-button card-link-accent"
              >
                ✉️ Drafter un email
              </button>
            </div>

            <div v-if="draftOpen[action.id]" class="draft-editor">
              <div class="draft-editor-header">
                <span class="draft-editor-label">✉️ Drafter un email — {{ action.contact_email }}</span>
              </div>
              <textarea
                v-model="draftInstructions[action.id]"
                rows="3"
                class="draft-instructions-textarea"
                :disabled="draftLoading[action.id]"
                placeholder="Instructions spécifiques (optionnel). Ex: 'insiste sur le mode Agent qu'il a essayé hier, propose une démo cette semaine'"
              ></textarea>
              <div class="draft-instructions-buttons">
                <button
                  @click="generateDraft(action)"
                  :disabled="draftLoading[action.id]"
                  class="btn btn-custom"
                >{{ draftLoading[action.id] ? 'Génération…' : (draftResult[action.id] ? 'Regénérer' : 'Générer') }}</button>
                <button @click="cancelDraft(action.id)" class="btn-undo">Fermer</button>
              </div>

              <div v-if="draftResult[action.id]" class="draft-result">
                <div class="draft-result-row">
                  <span class="draft-result-label">Sujet</span>
                  <span class="draft-result-subject">{{ draftResult[action.id].subject }}</span>
                </div>
                <div class="draft-result-row draft-result-body">{{ draftResult[action.id].body }}</div>
                <div class="draft-result-buttons">
                  <button @click="copyDraft(action.id)" class="btn btn-done">📋 Copier</button>
                  <a
                    :href="mailtoLink(action, draftResult[action.id])"
                    class="btn btn-defer"
                  >📧 Ouvrir dans mail</a>
                </div>
              </div>
            </div>

            <div v-if="descOpen[action.deal_id]" class="desc-editor">
              <div class="desc-editor-header">
                <span class="desc-editor-label">✎ Description Zoho — {{ action.deal_name }}</span>
                <span v-if="descLoading[action.deal_id]" class="desc-editor-meta">chargement…</span>
              </div>
              <textarea
                v-model="descDraft[action.deal_id]"
                rows="6"
                class="desc-textarea"
                :disabled="descLoading[action.deal_id]"
                placeholder="Description du deal — édite puis enregistre."
              ></textarea>
              <div class="desc-editor-buttons">
                <button
                  @click="saveDesc(action)"
                  :disabled="descLoading[action.deal_id] || descDraft[action.deal_id] === descOriginal[action.deal_id]"
                  class="btn btn-done"
                >Enregistrer dans Zoho</button>
                <button @click="cancelDesc(action.deal_id)" class="btn-undo">Annuler</button>
              </div>
            </div>

            <div v-if="action.analyse" class="action-block action-analyse">
              <div class="block-label">Analyse</div>
              <div class="block-body">{{ action.analyse }}</div>
            </div>

            <div v-if="action.next_step || action.draft_action" class="action-block action-next-step">
              <div class="block-label">Next step</div>
              <div class="block-body">{{ action.next_step || action.draft_action }}</div>
            </div>

            <div v-if="action.previously_recommended?.length" class="action-history">
              ⏱ Déjà recommandé : {{ action.previously_recommended.join(', ') }}
              <span v-if="action.previous_outcomes?.length"> → {{ action.previous_outcomes.join(', ') }}</span>
            </div>

            <div class="action-buttons" v-if="!loggedActions[action.id] && customOpen[action.id] !== true">
              <button @click="logAction(action, 'done')" class="btn btn-done">✅ Fait</button>
              <button @click="logAction(action, 'deferred')" class="btn btn-defer">⏭ Reporté</button>
              <button @click="logAction(action, 'dismissed')" class="btn btn-dismiss">❌ Pas pertinent</button>
              <button @click="openCustom(action.id)" class="btn btn-custom">✏️ Custom</button>
            </div>
            <div v-else-if="!loggedActions[action.id] && customOpen[action.id]" class="custom-form">
              <textarea
                v-model="customNotes[action.id]"
                placeholder="Décris ce que tu as fait (ex: 'appel pris, RDV calé jeudi 15h, intéressé par option Annuel')"
                rows="3"
                class="custom-textarea"
                ref="customTextarea"
              ></textarea>
              <div class="custom-buttons">
                <button @click="submitCustom(action)" :disabled="!customNotes[action.id]?.trim()" class="btn btn-done">Enregistrer</button>
                <button @click="cancelCustom(action.id)" class="btn-undo">Annuler</button>
              </div>
            </div>
            <div v-else class="action-logged-badge">
              <span class="logged-icon">{{ statusIcon(loggedActions[action.id]) }}</span>
              <span>{{ statusLabel(loggedActions[action.id]) }}</span>
              <span v-if="loggedNotes[action.id]" class="logged-notes">— {{ loggedNotes[action.id] }}</span>
              <button @click="undoAction(action.id)" class="btn-undo">Annuler</button>
            </div>
          </div>

          <p v-if="!section.actions?.length" class="empty-section">Aucune action.</p>
        </div>

        <details class="history-details">
          <summary class="history-summary">Historique 7 derniers jours ({{ history.actions?.length || 0 }} actions loguées)</summary>
          <div v-if="history.actions?.length" class="history-list">
            <div v-for="a in history.actions" :key="a.action_id + a.logged_at" class="history-item">
              <span :class="['hist-status', `hist-${a.status}`]">{{ statusIcon(a.status) }}</span>
              <span class="hist-date">{{ formatDate(a.date) }}</span>
              <span class="hist-action">{{ a.action_id }}</span>
              <span v-if="a.notes" class="hist-notes">— {{ a.notes }}</span>
            </div>
          </div>
        </details>
      </div>
    </div>
  </div>
</template>

<script>
import { supabase } from "@/supabase/supabaseClient";

const API_BASE = process.env.VUE_APP_API_URL || 'https://api.query-juriste.com';

export default {
  name: 'CoSellBriefPage',
  data() {
    return {
      owners: [
        { slug: 'lombardo', label: 'Lombardo' },
        { slug: 'roy', label: 'ROY' },
        { slug: 'mortagne', label: 'Mortagne' },
        { slug: 'etienne', label: 'Etienne' },
        { slug: 'martinez', label: 'Martinez' },
      ],
      selectedOwner: 'lombardo',
      today: new Date().toISOString().slice(0, 10),
      brief: null,
      history: { briefs: [], actions: [] },
      loggedActions: {},
      loggedNotes: {},
      customOpen: {},
      customNotes: {},
      descOpen: {},
      descLoading: {},
      descOriginal: {},
      descDraft: {},
      lostLoading: {},
      draftOpen: {},
      draftLoading: {},
      draftInstructions: {},
      draftResult: {},
      loading: false,
      adminPassword: null,
    };
  },
  async mounted() {
    await this.loadOwner(this.selectedOwner);
  },
  methods: {
    promptForPassword() {
      if (!this.adminPassword) {
        const p = prompt('Mot de passe administrateur requis :');
        if (!p) throw new Error('Mot de passe requis');
        this.adminPassword = p;
      }
      return this.adminPassword;
    },
    async selectOwner(slug) {
      this.selectedOwner = slug;
      await this.loadOwner(slug);
    },
    async loadOwner(slug) {
      this.loading = true;
      this.brief = null;
      try {
        const { data } = await supabase.auth.getSession();
        const jwt = data?.session?.access_token;
        if (!jwt) throw new Error('Session expirée');
        const password = this.promptForPassword();

        const headers = {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json',
          'X-Admin-Password': password,
        };

        const briefResp = await fetch(`${API_BASE}/copilot/admin/cosell/brief?owner=${slug}`, { headers });
        if (!briefResp.ok) {
          if (briefResp.status === 403) this.adminPassword = null;
          throw new Error((await briefResp.json()).error || 'Erreur brief');
        }
        const briefData = await briefResp.json();
        this.brief = briefData.brief;

        const histResp = await fetch(`${API_BASE}/copilot/admin/cosell/history?owner=${slug}&days=7`, { headers });
        if (histResp.ok) {
          this.history = await histResp.json();
        }

        // Reconstruct loggedActions + notes from history
        this.loggedActions = {};
        this.loggedNotes = {};
        for (const a of this.history.actions || []) {
          if (a.date === this.today && a.action_id) {
            this.loggedActions[a.action_id] = a.status;
            if (a.notes) this.loggedNotes[a.action_id] = a.notes;
          }
        }
      } catch (e) {
        console.error(e);
        this.$toast?.error?.(e.message || 'Erreur de chargement');
      } finally {
        this.loading = false;
      }
    },
    async logAction(action, status, notes) {
      try {
        const { data } = await supabase.auth.getSession();
        const jwt = data?.session?.access_token;
        const password = this.promptForPassword();

        const resp = await fetch(`${API_BASE}/copilot/admin/cosell/action`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${jwt}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            owner: this.selectedOwner,
            action_id: action.id,
            deal_id: action.deal_id,
            status,
            action_type: action.type,
            notes: notes || null,
            admin_password: password,
          }),
        });
        if (!resp.ok) {
          if (resp.status === 403) this.adminPassword = null;
          throw new Error((await resp.json()).error || 'Erreur logging');
        }
        this.loggedActions = { ...this.loggedActions, [action.id]: status };
        if (notes) this.loggedNotes = { ...this.loggedNotes, [action.id]: notes };
        this.$toast?.success?.(`Action ${this.statusLabel(status)}`);
      } catch (e) {
        console.error(e);
        this.$toast?.error?.(e.message || 'Erreur logging');
      }
    },
    openCustom(actionId) {
      this.customOpen = { ...this.customOpen, [actionId]: true };
      this.$nextTick(() => {
        const ta = this.$refs.customTextarea;
        if (Array.isArray(ta) ? ta[0] : ta) (Array.isArray(ta) ? ta[0] : ta).focus();
      });
    },
    cancelCustom(actionId) {
      this.customOpen = { ...this.customOpen, [actionId]: false };
      this.customNotes = { ...this.customNotes, [actionId]: '' };
    },
    async submitCustom(action) {
      const notes = (this.customNotes[action.id] || '').trim();
      if (!notes) return;
      await this.logAction(action, 'custom', notes);
      this.customOpen = { ...this.customOpen, [action.id]: false };
    },
    undoAction(actionId) {
      this.loggedActions = { ...this.loggedActions, [actionId]: null };
      this.loggedNotes = { ...this.loggedNotes, [actionId]: null };
    },
    async openDescEditor(action) {
      const dealId = action.deal_id;
      this.descLoading = { ...this.descLoading, [dealId]: true };
      this.descOpen = { ...this.descOpen, [dealId]: true };
      try {
        const { data } = await supabase.auth.getSession();
        const jwt = data?.session?.access_token;
        const password = this.promptForPassword();
        const resp = await fetch(`${API_BASE}/copilot/admin/cosell/zoho-deal?deal_id=${dealId}`, {
          headers: {
            'Authorization': `Bearer ${jwt}`,
            'Content-Type': 'application/json',
            'X-Admin-Password': password,
          },
        });
        if (!resp.ok) {
          if (resp.status === 403) this.adminPassword = null;
          throw new Error((await resp.json()).error || 'Erreur Zoho');
        }
        const deal = await resp.json();
        const current = deal.description || '';
        this.descOriginal = { ...this.descOriginal, [dealId]: current };
        this.descDraft = { ...this.descDraft, [dealId]: current };
      } catch (e) {
        console.error(e);
        this.$toast?.error?.(e.message || 'Erreur de chargement');
        this.descOpen = { ...this.descOpen, [dealId]: false };
      } finally {
        this.descLoading = { ...this.descLoading, [dealId]: false };
      }
    },
    cancelDesc(dealId) {
      this.descOpen = { ...this.descOpen, [dealId]: false };
      this.descDraft = { ...this.descDraft, [dealId]: '' };
    },
    async saveDesc(action) {
      const dealId = action.deal_id;
      const newDesc = this.descDraft[dealId];
      this.descLoading = { ...this.descLoading, [dealId]: true };
      try {
        const { data } = await supabase.auth.getSession();
        const jwt = data?.session?.access_token;
        const password = this.promptForPassword();
        const resp = await fetch(`${API_BASE}/copilot/admin/cosell/zoho-deal`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${jwt}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            deal_id: dealId,
            description: newDesc,
            admin_password: password,
          }),
        });
        if (!resp.ok) {
          if (resp.status === 403) this.adminPassword = null;
          throw new Error((await resp.json()).error || 'Erreur Zoho');
        }
        this.$toast?.success?.('Description mise à jour dans Zoho');
        this.descOpen = { ...this.descOpen, [dealId]: false };
        this.descOriginal = { ...this.descOriginal, [dealId]: newDesc };
      } catch (e) {
        console.error(e);
        this.$toast?.error?.(e.message || 'Erreur d\'enregistrement');
      } finally {
        this.descLoading = { ...this.descLoading, [dealId]: false };
      }
    },
    openDraftEmail(action) {
      this.draftOpen = { ...this.draftOpen, [action.id]: true };
    },
    cancelDraft(actionId) {
      this.draftOpen = { ...this.draftOpen, [actionId]: false };
    },
    async generateDraft(action) {
      this.draftLoading = { ...this.draftLoading, [action.id]: true };
      try {
        const { data } = await supabase.auth.getSession();
        const jwt = data?.session?.access_token;
        const password = this.promptForPassword();
        const resp = await fetch(`${API_BASE}/copilot/admin/cosell/draft-email`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${jwt}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            deal_id: action.deal_id,
            owner_slug: this.selectedOwner,
            action: action,
            instructions: this.draftInstructions[action.id] || '',
            admin_password: password,
          }),
        });
        if (!resp.ok) {
          if (resp.status === 403) this.adminPassword = null;
          throw new Error((await resp.json()).error || 'Erreur génération');
        }
        const out = await resp.json();
        this.draftResult = { ...this.draftResult, [action.id]: out };
      } catch (e) {
        console.error(e);
        this.$toast?.error?.(e.message || 'Erreur LLM');
      } finally {
        this.draftLoading = { ...this.draftLoading, [action.id]: false };
      }
    },
    async copyDraft(actionId) {
      const d = this.draftResult[actionId];
      if (!d) return;
      const text = `Sujet : ${d.subject}\n\n${d.body}`;
      try {
        await navigator.clipboard.writeText(text);
        this.$toast?.success?.('Email copié dans le presse-papiers');
      } catch {
        this.$toast?.error?.('Copie impossible (navigateur)');
      }
    },
    mailtoLink(action, draft) {
      const to = encodeURIComponent(draft.to || action.contact_email || '');
      const subject = encodeURIComponent(draft.subject || '');
      const body = encodeURIComponent(draft.body || '');
      return `mailto:${to}?subject=${subject}&body=${body}`;
    },
    async markLost(action) {
      const dealId = action.deal_id;
      if (!confirm(`Marquer "${action.deal_name}" comme PERDU dans Zoho ?`)) return;
      this.lostLoading = { ...this.lostLoading, [dealId]: true };
      try {
        const { data } = await supabase.auth.getSession();
        const jwt = data?.session?.access_token;
        const password = this.promptForPassword();
        const resp = await fetch(`${API_BASE}/copilot/admin/cosell/zoho-deal`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${jwt}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            deal_id: dealId,
            mark_lost: true,
            admin_password: password,
          }),
        });
        if (!resp.ok) {
          if (resp.status === 403) this.adminPassword = null;
          throw new Error((await resp.json()).error || 'Erreur Zoho');
        }
        this.$toast?.success?.('Deal marqué perdu dans Zoho');
        // Auto-log dismissed côté brief pour cohérence visuelle
        await this.logAction(action, 'dismissed', 'Marqué perdu dans Zoho');
      } catch (e) {
        console.error(e);
        this.$toast?.error?.(e.message || 'Erreur Zoho');
      } finally {
        this.lostLoading = { ...this.lostLoading, [dealId]: false };
      }
    },
    sectionClass(title) {
      if (title === 'URGENT') return 'sec-urgent';
      if (title === 'POWER USERS ÉMERGENTS') return 'sec-power';
      if (title === 'VISIOS À CONFIRMER') return 'sec-visio';
      if (title === 'DORMANTS À RÉVEILLER') return 'sec-dormant';
      if (title === 'À CLORE') return 'sec-close';
      if (title === 'POSITIF') return 'sec-positive';
      return '';
    },
    cabinetSearchTerm(action) {
      // For generic-domain prospects (gmail/icloud/etc), search by exact email.
      // For cabinet domains, search by the domain to see all collaborators.
      if (!action.contact_email && !action.domain) return null;
      const isGeneric = !!action.is_generic_domain || (action.domain && [
        'gmail.com','googlemail.com','icloud.com','me.com','mac.com',
        'hotmail.com','hotmail.fr','hotmail.it','outlook.com','outlook.fr',
        'yahoo.com','yahoo.fr','live.com','live.fr','aol.com',
        'proton.me','protonmail.com','free.fr','sfr.fr','orange.fr',
        'wanadoo.fr','laposte.net','avocat.fr',
      ].includes(action.domain));
      return isGeneric ? action.contact_email : action.domain;
    },
    statusLabel(s) {
      return { done: 'Fait', deferred: 'Reporté', dismissed: 'Pas pertinent', custom: 'Custom' }[s] || s;
    },
    statusIcon(s) {
      return { done: '✅', deferred: '⏭', dismissed: '❌', custom: '✏️' }[s] || '•';
    },
    formatAmount(n) {
      if (!n) return '';
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
    },
    formatDate(s) {
      if (!s) return '';
      return new Date(s).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    },
    formatTime(s) {
      if (!s) return '';
      return new Date(s).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    },
  },
};
</script>

<style scoped>
/* ────────────────────────────────────────────────────────────
   Co-Sell Brief — aligné sur le design system AdminDashboard
   ──────────────────────────────────────────────────────────── */

@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,700;0,9..144,900;1,9..144,400;1,9..144,700&family=IBM+Plex+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap');

.cosell-page {
  /* ── Palette alignée AdminDashboard ── */
  --ink:         #2d3748;
  --ink-soft:    #4a5568;
  --ink-muted:   #718096;
  --paper:       #ffffff;
  --paper-light: #f8faff;
  --paper-deep:  rgba(102, 126, 234, 0.06);
  --rule:        #e2e8f0;
  --rule-soft:   #f7fafc;

  --oxblood:       #667eea;
  --oxblood-deep:  #5a67d8;
  --oxblood-bg:    rgba(102, 126, 234, 0.1);

  --saffron:     #d97706;
  --saffron-bg:  rgba(251, 191, 36, 0.15);

  --moss:        #16a34a;
  --moss-bg:     rgba(134, 239, 172, 0.2);

  --brick:       #dc2626;
  --brick-bg:    rgba(252, 165, 165, 0.2);

  --slate:       #0891b2;
  --slate-bg:    rgba(125, 211, 252, 0.2);

  --ash:         #94a3b8;
  --ash-bg:      rgba(226, 232, 240, 0.5);

  --gold:        #d97706;

  /* ── Base ── */
  counter-reset: brief-edition 1;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem 1rem 4rem;
  font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  color: var(--ink);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.cosell-container {
  max-width: 1200px;
  margin: 0 auto;
}

/* ── HEADER ──────────────────────────────────────────────── */
.cosell-header {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: end;
  gap: 1.5rem;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
}

.header-content { min-width: 0; }

.page-title {
  display: flex;
  align-items: baseline;
  gap: 0.85rem;
  margin: 0 0 0.4rem;
  font-weight: 700;
  font-size: clamp(1.8rem, 4vw, 2rem);
  line-height: 1.1;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.page-title svg {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  color: rgba(255, 255, 255, 0.9);
  margin-top: 4px;
}

.page-subtitle {
  margin: 0;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 300;
}

.page-subtitle::before {
  content: 'N\00b0 ' counter(brief-edition) ' \00b7 ';
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
  white-space: nowrap;
  cursor: pointer;
  font-family: inherit;
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-1px);
  text-decoration: none;
  color: white;
}

.back-button svg { width: 14px; height: 14px; }

/* ── OWNER SELECTOR ──────────────────────────────────────── */
.owner-selector-card {
  display: flex;
  align-items: stretch;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 0;
  flex-wrap: wrap;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  margin-bottom: 1.75rem;
}

.owner-label {
  display: inline-flex;
  align-items: center;
  padding: 0.85rem 1.1rem 0.85rem 1.5rem;
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--ink-muted);
  text-transform: uppercase;
  letter-spacing: 0.22em;
  border-right: 1px solid var(--rule);
}

.owner-tabs {
  display: flex;
  gap: 0;
  flex: 1;
  flex-wrap: wrap;
  padding-left: 0.5rem;
}

.owner-tab {
  position: relative;
  padding: 0.85rem 1.1rem;
  border: none;
  background: transparent;
  color: var(--ink-muted);
  cursor: pointer;
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  transition: color 0.15s ease;
}

.owner-tab::before {
  content: '';
  position: absolute;
  left: 50%;
  bottom: 0;
  width: 0;
  height: 3px;
  background: var(--oxblood);
  transform: translateX(-50%);
  transition: width 0.25s cubic-bezier(.2,.8,.2,1);
  border-radius: 3px 3px 0 0;
}

.owner-tab:hover { color: var(--ink); }
.owner-tab:hover::before { width: 60%; }
.owner-tab.active {
  color: var(--oxblood);
  font-weight: 600;
}
.owner-tab.active::before { width: 100%; }

.date-display {
  display: inline-flex;
  align-items: center;
  margin-left: auto;
  padding: 0.85rem 1.5rem 0.85rem 1.1rem;
  font-style: italic;
  font-size: 0.9rem;
  color: var(--ink-soft);
  border-left: 1px solid var(--rule);
}

.date-display::before {
  content: '\2014 ';
  font-style: normal;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: var(--ink-muted);
  margin-right: 0.4rem;
}

/* ── LOADING / EMPTY ─────────────────────────────────────── */
.loading-state,
.empty-state {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 4rem 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-align: center;
  color: var(--ink-soft);
}

.empty-state p {
  font-family: 'Fraunces', Georgia, serif;
  font-size: 1.1rem;
  font-style: italic;
  margin: 0 0 0.5rem;
  color: var(--ink-soft);
}

.empty-hint {
  font-style: normal !important;
  font-size: 0.82rem !important;
  color: var(--ink-muted) !important;
  font-family: 'IBM Plex Sans', sans-serif !important;
  display: block;
  margin-top: 0.5rem;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(102, 126, 234, 0.2);
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
  margin: 0 auto 1.2rem;
}

@keyframes spin { to { transform: rotate(360deg); } }

/* ── BRIEF CONTENT ───────────────────────────────────────── */
.brief-content {
  counter-reset: section-num;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* ── SUMMARY ─────────────────────────────────────────────── */
.summary-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 1.75rem 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-left: 4px solid var(--oxblood);
}

.summary-text {
  margin: 0;
  font-family: 'Fraunces', Georgia, serif;
  font-size: 1.1rem;
  font-weight: 400;
  line-height: 1.6;
  color: var(--ink);
}

.summary-meta {
  margin: 0.8rem 0 0;
  padding-top: 0.75rem;
  border-top: 1px solid var(--rule);
  font-size: 0.72rem;
  color: var(--ink-muted);
  text-transform: uppercase;
  letter-spacing: 0.15em;
  font-weight: 500;
}

.summary-meta::before {
  content: '\25e6 ';
  color: var(--gold);
}

/* ── SECTIONS ────────────────────────────────────────────── */
.section-card {
  counter-increment: section-num;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
}

.section-heading {
  margin: 0;
  padding: 1rem 1.5rem 0.9rem;
  font-weight: 600;
  font-size: 0.8rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border-bottom: 1px solid var(--rule);
  background: rgba(102, 126, 234, 0.06);
  color: var(--ink-soft);
}

.section-heading::before {
  content: counter(section-num, decimal-leading-zero);
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.72rem;
  letter-spacing: 0;
  font-weight: 600;
  padding: 0.2rem 0.5rem;
  background: var(--oxblood);
  color: white;
  border-radius: 4px;
}

.section-heading::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--ink);
  opacity: 0.1;
}

.section-count {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.7rem;
  letter-spacing: 0;
  text-transform: none;
  color: var(--ink-muted);
  font-weight: 400;
  margin-left: auto;
}

/* Couleurs sémantiques par section */
.section-heading.sec-urgent    { color: var(--brick); background: rgba(220, 38, 38, 0.06); }
.section-heading.sec-urgent::before    { background: var(--brick); }
.section-heading.sec-power     { color: var(--moss); background: rgba(22, 163, 74, 0.06); }
.section-heading.sec-power::before     { background: var(--moss); }
.section-heading.sec-visio     { color: var(--slate); background: rgba(8, 145, 178, 0.06); }
.section-heading.sec-visio::before     { background: var(--slate); }
.section-heading.sec-dormant   { color: var(--saffron); background: rgba(217, 119, 6, 0.06); }
.section-heading.sec-dormant::before   { background: var(--saffron); }
.section-heading.sec-close     { color: var(--ash); background: rgba(148, 163, 184, 0.06); }
.section-heading.sec-close::before     { background: var(--ash); }
.section-heading.sec-positive  { color: var(--moss); background: rgba(22, 163, 74, 0.06); }
.section-heading.sec-positive::before  { background: var(--moss); }

/* Accent gauche par section (remplace l'inset box-shadow) */
.section-card:has(.sec-urgent)   { border-left: 4px solid var(--brick); }
.section-card:has(.sec-power)    { border-left: 4px solid var(--moss); }
.section-card:has(.sec-visio)    { border-left: 4px solid var(--slate); }
.section-card:has(.sec-dormant)  { border-left: 4px solid var(--saffron); }
.section-card:has(.sec-close)    { border-left: 4px solid var(--ash); }
.section-card:has(.sec-positive) { border-left: 4px solid var(--moss); }

/* ── ACTION CARD ─────────────────────────────────────────── */
.action-card {
  position: relative;
  padding: 1.25rem 1.5rem 1.25rem 1.75rem;
  background: var(--paper);
  border: none;
  border-bottom: 1px solid var(--rule-soft);
  transition: background 0.2s ease;
}

.action-card:last-child { border-bottom: none; }

.action-card:hover {
  background: var(--paper-light);
}

.action-card.is-logged {
  background: var(--paper-deep);
  opacity: 0.75;
}

.action-card.is-logged::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 1.5rem;
  right: 1.5rem;
  height: 1px;
  background: var(--ink-muted);
  opacity: 0.3;
  pointer-events: none;
}

/* ── Card top ────────────────────────────────────────────── */
.card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.25rem;
  margin-bottom: 0.85rem;
  padding-bottom: 0.8rem;
  border-bottom: 1px solid var(--rule);
}

.card-identity {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 0;
}

.card-org {
  font-family: 'Fraunces', Georgia, serif;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--ink);
  line-height: 1.2;
  letter-spacing: -0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-email {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.78rem;
  color: var(--oxblood);
  text-decoration: none;
  font-weight: 500;
  word-break: break-all;
  transition: opacity 0.15s ease;
  width: fit-content;
  max-width: 100%;
}

.card-email:hover {
  text-decoration: underline;
  opacity: 0.8;
}

.card-amount {
  font-family: 'JetBrains Mono', monospace;
  font-weight: 600;
  color: var(--saffron);
  white-space: nowrap;
  font-size: 1rem;
  padding: 0.1rem 0 0.1rem 0.85rem;
  border-left: 3px solid var(--saffron);
  height: fit-content;
  align-self: center;
  letter-spacing: -0.01em;
}

.card-headline {
  color: var(--ink-soft);
  font-size: 0.92rem;
  margin-bottom: 0.85rem;
  line-height: 1.55;
  font-weight: 400;
}

/* ── Card links ──────────────────────────────────────────── */
.card-links {
  display: flex;
  gap: 0;
  margin-bottom: 0.9rem;
  flex-wrap: wrap;
  align-items: center;
}

.card-link {
  font-size: 0.72rem;
  color: var(--ink-muted);
  text-decoration: none;
  background: transparent;
  padding: 0;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  transition: color 0.15s ease;
}

.card-link:hover {
  color: var(--oxblood);
  text-decoration: none;
  background: transparent;
}

.card-link + .card-link::before {
  content: '\00b7';
  margin: 0 0.7rem;
  color: var(--rule);
}

.card-link-button {
  font-family: inherit;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
}

.card-link-button:disabled {
  opacity: 0.4;
  cursor: wait;
}

.card-link-danger:hover { color: var(--brick); }
.card-link-accent { color: var(--moss); }
.card-link-accent:hover { color: var(--moss); opacity: 0.8; }

/* ── Draft editor ────────────────────────────────────────── */
.draft-editor {
  margin: 0.75rem 0;
  padding: 0.9rem 1.1rem;
  background: var(--moss-bg);
  border: 1px solid rgba(22, 163, 74, 0.3);
  border-radius: 8px;
  position: relative;
}

.draft-editor-header { margin-bottom: 0.5rem; }

.draft-editor-label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--moss);
}

.draft-instructions-textarea {
  width: 100%;
  padding: 0.65rem 0.85rem;
  border: 1px solid var(--rule);
  background: white;
  border-radius: 6px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 0.85rem;
  line-height: 1.45;
  resize: vertical;
  min-height: 60px;
  box-sizing: border-box;
  color: var(--ink);
}

.draft-instructions-textarea:focus {
  outline: none;
  border-color: var(--moss);
  box-shadow: 0 0 0 2px rgba(22, 163, 74, 0.15);
}

.draft-instructions-textarea::placeholder {
  color: var(--ink-muted);
  font-style: italic;
}

.draft-instructions-buttons {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.55rem;
  align-items: center;
}

.draft-result {
  margin-top: 0.85rem;
  padding: 1rem 1.1rem;
  background: white;
  border: 1px solid var(--rule);
  border-left: 3px solid var(--moss);
  border-radius: 6px;
}

.draft-result-row {
  display: flex;
  gap: 0.85rem;
  align-items: baseline;
  margin-bottom: 0.6rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--rule);
}

.draft-result-label {
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--ink-muted);
  flex-shrink: 0;
}

.draft-result-subject {
  font-family: 'Fraunces', Georgia, serif;
  font-size: 1rem;
  font-weight: 600;
  color: var(--ink);
  line-height: 1.3;
}

.draft-result-body {
  border-bottom: none;
  padding-bottom: 0;
  font-family: 'Fraunces', Georgia, serif;
  font-size: 0.95rem;
  line-height: 1.55;
  color: var(--ink);
  white-space: pre-wrap;
  margin-bottom: 0.85rem;
  display: block;
}

.draft-result-buttons {
  display: flex;
  gap: 0.5rem;
}

.draft-result-buttons a.btn {
  text-decoration: none;
  display: inline-flex;
  align-items: center;
}

/* ── Desc editor ─────────────────────────────────────────── */
.desc-editor {
  margin: 0.75rem 0;
  padding: 0.9rem 1.1rem;
  background: var(--oxblood-bg);
  border: 1px solid rgba(102, 126, 234, 0.3);
  border-radius: 8px;
  position: relative;
}

.desc-editor-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.6rem;
}

.desc-editor-label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--oxblood);
}

.desc-editor-meta {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.7rem;
  color: var(--ink-muted);
}

.desc-textarea {
  width: 100%;
  padding: 0.7rem 0.85rem;
  border: 1px solid var(--rule);
  background: white;
  border-radius: 6px;
  font-family: 'Fraunces', Georgia, serif;
  font-size: 0.95rem;
  line-height: 1.5;
  resize: vertical;
  min-height: 100px;
  box-sizing: border-box;
  color: var(--ink);
}

.desc-textarea:focus {
  outline: none;
  border-color: var(--oxblood);
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.15);
}

.desc-editor-buttons {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 0.7rem;
}

/* ── Action blocks ───────────────────────────────────────── */
.action-block {
  background: var(--paper-light);
  padding: 0.75rem 1.1rem 0.85rem;
  margin-bottom: 0.6rem;
  border-radius: 6px;
  border: 1px solid var(--rule);
  position: relative;
}

.action-block .block-label {
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  margin-bottom: 0.4rem;
  display: inline-block;
  padding: 0.15rem 0.5rem;
  background: var(--ink);
  color: white;
  border-radius: 3px;
}

.action-block .block-body {
  font-family: 'Fraunces', Georgia, serif;
  font-size: 0.95rem;
  line-height: 1.55;
  color: var(--ink);
  white-space: pre-wrap;
}

.action-analyse {
  border-left: 3px solid var(--slate);
}

.action-analyse .block-label {
  background: var(--slate);
}

.action-next-step {
  border-left: 3px solid var(--oxblood);
}

.action-next-step .block-label {
  background: var(--oxblood);
}

.action-next-step .block-body::before {
  content: '\27a4  ';
  color: var(--oxblood);
  font-weight: 700;
  font-family: 'IBM Plex Sans', sans-serif;
}

/* Legacy: garder .action-draft au cas où un brief utilise encore l'ancienne clé */
.action-draft {
  background: var(--paper-light);
  padding: 1rem 1.15rem;
  border: 1px solid var(--rule);
  border-left: 3px solid var(--oxblood);
  border-radius: 6px;
  font-family: 'Fraunces', Georgia, serif;
  font-size: 0.97rem;
  line-height: 1.55;
  color: var(--ink);
  white-space: pre-wrap;
  margin-bottom: 0.85rem;
}

.action-history {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.7rem;
  color: var(--ash);
  margin-bottom: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 0.4rem 0.6rem;
  background: var(--ash-bg);
  border-left: 2px solid var(--ash);
  border-radius: 0 4px 4px 0;
}

/* ── Buttons ─────────────────────────────────────────────── */
.action-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  background: var(--paper-deep);
  color: var(--ink-soft);
  border-radius: 8px;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  transition: all 0.2s ease;
  position: relative;
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.btn:active {
  transform: translateY(0);
  box-shadow: none;
}

.btn-done {
  background: var(--moss);
  color: white;
}

.btn-done:hover { background: #15803d; }

.btn-defer {
  background: var(--saffron-bg);
  color: var(--saffron);
  border: 1px solid rgba(217, 119, 6, 0.3);
}

.btn-defer:hover { background: rgba(251, 191, 36, 0.25); }

.btn-dismiss {
  background: var(--ash-bg);
  color: var(--ash);
  border: 1px solid rgba(148, 163, 184, 0.3);
}

.btn-dismiss:hover { background: rgba(148, 163, 184, 0.2); }

.btn-custom {
  background: var(--oxblood-bg);
  color: var(--oxblood);
  border: 1px solid rgba(102, 126, 234, 0.3);
}

.btn-custom:hover { background: rgba(102, 126, 234, 0.2); }

.btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none !important;
}

/* ── Custom form ─────────────────────────────────────────── */
.custom-form {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 1rem 1.1rem;
  background: rgba(251, 191, 36, 0.1);
  border: 1px solid rgba(217, 119, 6, 0.25);
  border-radius: 8px;
  position: relative;
}

.custom-form::before {
  content: '\270e  NOTE';
  position: absolute;
  top: -9px;
  left: 12px;
  background: white;
  padding: 0 0.5rem;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  color: var(--saffron);
}

.custom-textarea {
  width: 100%;
  padding: 0.7rem 0.85rem;
  border: 1px solid var(--rule);
  background: white;
  border-radius: 6px;
  font-family: 'Fraunces', Georgia, serif;
  font-size: 0.95rem;
  line-height: 1.5;
  resize: vertical;
  min-height: 70px;
  box-sizing: border-box;
  color: var(--ink);
}

.custom-textarea:focus {
  outline: none;
  border-color: var(--saffron);
  box-shadow: 0 0 0 2px rgba(217, 119, 6, 0.15);
}

.custom-textarea::placeholder {
  color: var(--ink-muted);
  font-style: italic;
}

.custom-buttons {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  align-items: center;
}

/* ── Logged badge ────────────────────────────────────────── */
.action-logged-badge {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.65rem 0.9rem;
  background: rgba(22, 163, 74, 0.08);
  border: 1px solid rgba(22, 163, 74, 0.25);
  border-radius: 8px;
  font-size: 0.78rem;
  color: var(--ink-soft);
  font-weight: 500;
}

.logged-icon { font-size: 0.95rem; }

.logged-notes {
  flex: 1;
  font-family: 'Fraunces', Georgia, serif;
  font-style: italic;
  font-weight: 400;
  color: var(--ink-soft);
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.btn-undo {
  margin-left: auto;
  background: transparent;
  border: 1px solid var(--rule);
  padding: 0.3rem 0.7rem;
  border-radius: 6px;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.68rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--ink-muted);
  transition: all 0.15s ease;
}

.btn-undo:hover {
  border-color: var(--ink);
  color: var(--ink);
}

.empty-section {
  text-align: center;
  padding: 1.5rem;
  color: var(--ink-muted);
  font-family: 'Fraunces', Georgia, serif;
  font-style: italic;
  margin: 0;
  font-size: 0.95rem;
}

/* ── Historique ──────────────────────────────────────────── */
.history-details {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 0.75rem 1.75rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.history-details[open] { padding-bottom: 1.5rem; }

.history-summary {
  cursor: pointer;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--ink-muted);
  text-transform: uppercase;
  letter-spacing: 0.2em;
  list-style: none;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.4rem 0;
}

.history-summary::-webkit-details-marker { display: none; }

.history-summary::before {
  content: '\25b8';
  font-size: 0.75rem;
  color: var(--oxblood);
  transition: transform 0.2s ease;
}

.history-details[open] .history-summary::before {
  transform: rotate(90deg);
}

.history-list {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0;
  border-top: 1px solid var(--rule);
}

.history-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.82rem;
  color: var(--ink-soft);
  padding: 0.6rem 0;
  border-bottom: 1px solid var(--rule-soft);
}

.history-item:last-child { border-bottom: none; }

.hist-status {
  font-size: 0.95rem;
  width: 22px;
  display: inline-flex;
  justify-content: center;
}

.hist-date {
  font-family: 'JetBrains Mono', monospace;
  font-weight: 500;
  color: var(--ink);
  min-width: 130px;
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.hist-action {
  color: var(--ash);
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.72rem;
}

.hist-notes {
  color: var(--ink-soft);
  font-family: 'Fraunces', Georgia, serif;
  font-style: italic;
  font-size: 0.88rem;
}

/* ── Responsive ──────────────────────────────────────────── */
@media (max-width: 720px) {
  .cosell-page { padding: 1.5rem 0.75rem 3rem; }
  .page-title { font-size: 1.7rem; }
  .cosell-header {
    grid-template-columns: 1fr;
    align-items: start;
  }
  .back-button { justify-self: start; }
  .owner-selector-card {
    flex-direction: column;
    align-items: stretch;
  }
  .owner-label {
    border-right: none;
    border-bottom: 1px solid var(--rule);
    padding: 0.6rem 1.5rem;
  }
  .owner-tabs { padding-left: 0; }
  .date-display {
    margin-left: 0;
    border-left: none;
    border-top: 1px solid var(--rule);
    padding-left: 1.5rem;
  }
  .card-top {
    flex-direction: column;
    gap: 0.5rem;
  }
  .card-amount { align-self: flex-start; }
  .action-card { padding: 1rem 1rem 1rem 1.25rem; }
}
</style>